"use client";

import { useCallback, useMemo, useState } from "react";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { splitLegacyClearanceHighlights } from "@/features/search/lib/clearances";
import { useHighlightEntries } from "@/features/search/lib/highlights";

const HIGHLIGHTS_COLLAPSED_COUNT = 6;

export function FilterHighlightsSection() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const selected = filters.highlight_types;
  const selectedValues = useMemo(
    () => splitLegacyClearanceHighlights(selected).highlights,
    [selected],
  );
  const [expanded, setExpanded] = useState(false);
  const { entries, isLoading } = useHighlightEntries();

  const toggle = useCallback(
    (type: string) => {
      const current = filters.highlight_types ?? [];
      const idx = current.findIndex((h) => h.text === type);
      const next =
        idx >= 0
          ? current.filter((_, i) => i !== idx)
          : [...current, { text: type, required: false }];
      updateFilter("highlight_types", next.length > 0 ? next : undefined);
    },
    [filters.highlight_types, updateFilter],
  );

  const toggleRequired = useCallback(
    (type: string) => {
      const current = filters.highlight_types ?? [];
      const idx = current.findIndex((h) => h.text === type);
      if (idx < 0) return;
      const next = [...current];
      next[idx] = { ...next[idx], required: !next[idx].required };
      updateFilter("highlight_types", next);
    },
    [filters.highlight_types, updateFilter],
  );

  const sorted = useMemo(() => {
    const isActive = (t: string) => selectedValues.some((s) => s.text === t);
    const active = entries.filter((e) => isActive(e.type));
    const inactive = entries.filter((e) => !isActive(e.type));
    return [...active, ...inactive];
  }, [entries, selectedValues]);

  const visible = expanded
    ? sorted
    : sorted.slice(
        0,
        Math.max(HIGHLIGHTS_COLLAPSED_COUNT, selectedValues.length),
      );
  const hasMore = sorted.length > visible.length;

  if (isLoading && entries.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        {Array.from({ length: HIGHLIGHTS_COLLAPSED_COUNT }).map((_, i) => (
          <div
            key={i}
            className="h-8 bg-contrast/3 border border-border rounded-md shimmer shimmer-bg text-foreground"
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        {visible.map(({ type, icon, label, color }) => {
          const entry = selectedValues.find((s) => s.text === type);
          const isChecked = !!entry;
          const isRequired = entry?.required ?? false;

          const requiredLabel = isRequired
            ? "Required (every required entity must match)"
            : "Optional (any optional entity may match)";

          return (
            <div
              key={type}
              className="flex items-center gap-2 h-8 bg-contrast/3 border border-border rounded-md px-2.5 hover:bg-contrast/5 transition-colors"
            >
              <button
                type="button"
                onClick={() => toggle(type)}
                className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
              >
                <Icon name={icon as IconName} size={14} style={{ color }} />
                <span className="flex-1 text-[13px] text-contrast/70 text-left truncate">
                  {label}
                </span>
              </button>
              {isChecked && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRequired(type);
                      }}
                      aria-pressed={isRequired}
                      aria-label={requiredLabel}
                      tabIndex={0}
                      className="shrink-0 cursor-pointer transition-opacity"
                    >
                      <Icon
                        name="IconAsterisk"
                        size={12}
                        className={
                          isRequired
                            ? "text-ai"
                            : "text-contrast opacity-30 hover:opacity-60"
                        }
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center">
                    {requiredLabel}
                  </TooltipContent>
                </Tooltip>
              )}
              <Checkbox checked={isChecked} onChange={() => toggle(type)} />
            </div>
          );
        })}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs font-medium text-ai cursor-pointer hover:text-ai/80 dark:hover:text-[#ffbf73] transition-colors text-left"
        >
          {expanded
            ? "See less"
            : `See ${sorted.length - HIGHLIGHTS_COLLAPSED_COUNT} more`}
        </button>
      )}
    </>
  );
}
